"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/feedback";
import { useAccessToken, useTenantData } from "@/hooks/shared";
import { invalidateUserTaskQueries } from "@/lib/user-tasks/invalidate-user-task-queries";
import { buildCreateUserTaskApiRequest } from "@/lib/user-tasks/build-create-user-task-request";
import { buildCreateUserTasksBulkBody } from "@/lib/user-tasks/build-create-user-tasks-bulk-request";
import { createAuthenticatedApi } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/utils/api/error-message";
import { UserTasksApi } from "@/openapi/generated";

export type CreateUserTaskInput = {
  title: string;
  assigneeUserIds: number[];
  description?: string | null;
  dueAt?: string | null;
};

export function useCreateUserTask() {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const queryClient = useQueryClient();
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: async (input: CreateUserTaskInput) => {
      if (!accessToken || tenantId == null) {
        throw new Error("Authentication required");
      }

      const assigneeUserIds = input.assigneeUserIds.filter(
        (id) => Number.isFinite(id) && id > 0,
      );
      if (assigneeUserIds.length === 0) {
        throw new Error("At least one assignee is required");
      }

      if (assigneeUserIds.length === 1) {
        const service = createAuthenticatedApi(UserTasksApi, accessToken);
        const body = buildCreateUserTaskApiRequest({
          title: input.title,
          assigneeUserId: assigneeUserIds[0],
          description: input.description,
          dueAt: input.dueAt,
        });
        await service.apiUserTasksPost({
          createUserTaskRequest: body,
          xTenantID: tenantId,
        });
        return { count: 1 };
      }

      const service = createAuthenticatedApi(UserTasksApi, accessToken);
      const body = buildCreateUserTasksBulkBody({
        title: input.title,
        assigneeUserIds,
        description: input.description,
        dueAt: input.dueAt,
      });
      const result = await service.apiUserTasksBulkPost({
        createUserTasksBulkRequest: body,
        xTenantID: tenantId,
      });
      return { count: result.taskIds.length };
    },
    onSuccess: async (result) => {
      await invalidateUserTaskQueries(queryClient, tenantId);
      const count = result.count;
      toast({
        title: count === 1 ? "Task created" : `${count} tasks created`,
        description:
          count === 1
            ? "The assignee will see it on their dashboard and in My Tasks."
            : "Each assignee will see their own task on the dashboard and in My Tasks.",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      toast({
        title: "Could not create task",
        description: await getApiErrorMessage(error),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return {
    createUserTask: mutation.mutate,
    createUserTaskAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
  };
}
